import React from "react";
import { useMutation } from "@apollo/react-hooks";
import { Field, TextInput } from "@streeteasy/design-system";
import { TextArea } from "@streeteasy/design-system/form/TextAreaField";
import { Formik } from "formik";
import { func, number, oneOfType, string } from "prop-types";

import deviceFingerprint from "src/shared/utilities/deviceFingerprint";

import { agentMatchItemPropType } from "../../../../propTypes";
import { useContactFormData } from "../../redux/hooks/contactFormHooks";
import { useUIBehavior } from "../../redux/hooks/uiHooks";
import CREATE_LISTING_AGENT_MATCH_CONNECTION from "../../redux/mutations/createListingAgentMatchConnection";
import { agentContactForm } from "../../schemas/agentContactForm";
import * as S from "./styled";

const AgentContactForm = ({
  agentMatchItem,
  className,
  handleCancelClick,
  propertyId,
  userId
}) => {
  const {
    email,
    errors: externalValidationErrors,
    name,
    phoneNumber
  } = useContactFormData();
  const { setModalViewType } = useUIBehavior();

  const handleAnalyticsFormSubmit = () => {
    window.dataLayer.push({
      event: "custom_event",
      event_category: "homepage",
      event_action: "contact_listing_agent",
      event_label: `expert_contact_id=${agentMatchItem.contactId}|uuid=${agentMatchItem.uuid}|property_id=${propertyId}`
    });
  };

  const [createListingAgentMatchConnection, { error }] = useMutation(
    CREATE_LISTING_AGENT_MATCH_CONNECTION,
    {
      onCompleted: () => {
        setModalViewType("success");
        handleAnalyticsFormSubmit();
      },
      onError: value => {
        if (!value.message.includes("phone")) {
          setModalViewType("error");
        }
      }
    }
  );

  return (
    <S.FormContainer className={className}>
      <Formik
        initialValues={{
          email,
          name,
          phoneNumber,
          message: "Hi, I'd like to discuss selling my home."
        }}
        onSubmit={values => {
          createListingAgentMatchConnection({
            variables: {
              agent_name: agentMatchItem.name,
              brokerage: agentMatchItem.brokerage,
              brokerage_address: agentMatchItem.brokerageAddress,
              contact_id: agentMatchItem.contactId,
              email: values.email,
              fingerprint_or_device_guid: deviceFingerprint(),
              has_sold_in_building: agentMatchItem.hasSoldInBuilding,
              homes_sold_nearby: agentMatchItem.homesSoldNearby,
              homes_sold_recent: agentMatchItem.homesSoldRecent,
              homes_sold_similar: agentMatchItem.homesSoldSimilar,
              is_pro: agentMatchItem.isPro,
              license_type: agentMatchItem.licenseType,
              median_sales_price: agentMatchItem.medianSalesPrice,
              name: values.name,
              notes: values.message,
              origin_label: "listing_agent_match_homepage",
              phone: values.phoneNumber,
              photo_url: agentMatchItem.photoUrl,
              property_id: propertyId,
              user_id: userId,
              uuid: agentMatchItem.uuid,
              years_experience: agentMatchItem.yearsExperience
            }
          });
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={agentContactForm}
      >
        {({ errors, handleChange, handleSubmit, isSubmitting, values }) => (
          <S.Form>
            <div>
              <S.FieldContainer>
                <Field
                  error={errors.name}
                  hint="required"
                  id="name"
                  isValid={!errors.name}
                  label="Name"
                >
                  <TextInput
                    name="name"
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.name}
                  />
                </Field>
              </S.FieldContainer>
              <S.FieldContainer>
                <Field
                  error={
                    errors.phoneNumber ||
                    error?.message
                      .split(" ")
                      .slice(2)
                      .join(" ")
                  }
                  hint="required"
                  id="phoneNumber"
                  isValid={!errors.phoneNumber && !error?.message}
                  label="Phone"
                  onChange={handleChange}
                >
                  <TextInput
                    name="phoneNumber"
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.phoneNumber}
                  />
                </Field>
              </S.FieldContainer>
              <S.FieldContainer>
                <Field
                  error={errors.email || externalValidationErrors?.email}
                  hint="required"
                  id="email"
                  isValid={!errors.email && !externalValidationErrors?.email}
                  label="Email"
                >
                  <TextInput
                    name="email"
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.email}
                  />
                </Field>
              </S.FieldContainer>
              <S.FieldContainer>
                <Field
                  error={errors.message}
                  hint="required"
                  id="message"
                  isValid={!errors.message}
                  label="Message"
                >
                  <TextArea
                    name="message"
                    onChange={handleChange}
                    required
                    value={values.message}
                  />
                </Field>
              </S.FieldContainer>
              <S.BrokerageAddress>
                Broker at {agentMatchItem.brokerageAddress}
              </S.BrokerageAddress>
            </div>
            <S.TermsMessage />
            <S.ButtonContainer>
              <S.CancelButton
                disabled={isSubmitting && !error?.message}
                onClick={handleCancelClick}
                type="button"
              >
                Cancel
              </S.CancelButton>
              <S.SubmitButton
                disabled={isSubmitting && !error?.message}
                onClick={handleSubmit}
                type="submit"
              >
                Send Message
              </S.SubmitButton>
            </S.ButtonContainer>
          </S.Form>
        )}
      </Formik>
    </S.FormContainer>
  );
};

AgentContactForm.propTypes = {
  agentMatchItem: agentMatchItemPropType.isRequired,
  className: string,
  handleCancelClick: func.isRequired,
  propertyId: number.isRequired,
  userId: oneOfType([number, string]).isRequired
};

AgentContactForm.defaultProps = {
  className: null
};

export default AgentContactForm;
